// src/services/logic/creaVersamenti.ts

import { addDoc, collection, getDoc, getDocs, query, QueryConstraint, where } from "firebase/firestore";
import { db } from "../../firebase";
import { InitialAnalysisResult, DeeperAnalysisResult } from "../types/analysisTypes";
import {
  toRate,
  toRatePagata,
  toRateRiscaldamento,
  toRateRiscaldamentoPagata,
} from "../converters/firestoreConverters";
import { DocumentSnapshot } from "firebase/firestore";
import { Rate, RatePagata, RateRiscaldamento, RateRiscaldamentoPagata, VersamentoEnriched, VersamentoRate, VersamentoRiscaldamento } from "../types/firestoreTypes";

type FeeKey = "GO" | "GS" | "RISC";
// --- Generic helper that takes arbitrary query constraints ---
async function fetchAndConvertQuery<T>(
  condominioId: string,
  subcol: string,
  converter: (doc: DocumentSnapshot) => T,
  ...constraints: QueryConstraint[]
): Promise<T[]> {
  const q = query(
    collection(db, "condomini", condominioId, subcol),
    ...constraints
  );
  const snap = await getDocs(q);
  return snap.docs.map(converter);
}

// --- Balance computation types & helper ---
interface BalanceDetail {
  paid: number;
  unpaid: number;
  totalDue: number;
  dueNow: number;
  dueFuture: number;
  balance: number;
}


/**
 * Computes detailed balances from scheduled fees and actual transactions.
 */
function computeBalances(
  ordinaryScheduled: Rate[],
  ordinaryTransactions: RatePagata[],
  heatingScheduled: RateRiscaldamento[],
  heatingTransactions: RateRiscaldamentoPagata[],
  today: Date = new Date()
): Record<'GO' | 'GS' | 'RISC', BalanceDetail> {



  function computeDetail<
    S extends { imp_tot: number },
    P extends { imp_tot: number }
  >(
    scheduledList: S[],
    transactionList: P[],
    getSchedDate: (r: S) => Date,
    getTxnDate: (r: P) => Date,
    filterScheduled?: (r: S) => boolean,
    filterTransactions?: (r: P) => boolean
  ): BalanceDetail {
    const sched = filterScheduled
      ? scheduledList.filter(filterScheduled)
      : scheduledList;
    const txns = filterTransactions
      ? transactionList.filter(filterTransactions)
      : transactionList;

    const totalScheduled = sched.reduce((s, r) => s + r.imp_tot, 0);
    const totalTransacted =
      txns.reduce((s, r) => s + r.imp_tot, 0);

    const unpaid = totalScheduled - totalTransacted;
    const dueNow =
      sched.filter(r => getSchedDate(r) <= today).reduce((s, r) => s + r.imp_tot, 0)
      - txns.filter(r => getTxnDate(r) <= today).reduce((s, r) => s + r.imp_tot, 0);
    const dueFuture =
      sched.filter(r => getSchedDate(r) > today).reduce((s, r) => s + r.imp_tot, 0)
      - txns.filter(r => getTxnDate(r) > today).reduce((s, r) => s + r.imp_tot, 0);

    return {
      paid: totalTransacted,
      unpaid,
      totalDue: totalScheduled,
      dueNow,
      dueFuture,
      balance: unpaid,
    };
  }


  return {
    GO: computeDetail(
      ordinaryScheduled,
      ordinaryTransactions,
      r => new Date(r.data_scad),   // scheduled rates use data_scad
      r => new Date(r.data_reg),    // paid rates use data_reg
      r => r.tipo_rata === "GO",
      r => r.tipo_rata === "VO"
    ),

    GS: computeDetail(
      ordinaryScheduled,
      ordinaryTransactions,
      r => new Date(r.data_scad),
      r => new Date(r.data_reg),
      r => r.tipo_rata === "GS",
      r => r.tipo_rata === "VS"
    ),

    RISC: computeDetail(
      heatingScheduled,
      heatingTransactions,
      r => new Date(r.data_scad),
      r => new Date(r.data_reg)
    ),
  };

}




/**
 * Creates payment entries (versamenti) based on analysis results.
 */
export async function creaVersamenti(
  condominioId: string,
  initialAnalysis: InitialAnalysisResult,
  deeperAnalysis: DeeperAnalysisResult
): Promise<VersamentoRiscaldamento[] | VersamentoRate[] | VersamentoEnriched[]> {
  console.log("Creating payment entries (versamenti) for condominium:", condominioId);
  console.log("Initial Analysis:", initialAnalysis);
  console.log("Deeper Analysis:", deeperAnalysis);

  console.log("Starting versamenti creation process...");
  // 1. Determine fee types present from initialAnalysis
  const fee_types = Array.from(
    new Set(
      initialAnalysis.fees
        .map((f) => f.type)
        .filter((t): t is 'GO' | 'GS' | 'RISC' => t !== '')
    )
  );
  console.log("Fee types found in the transaction:", fee_types);

  if (fee_types.length === 0) {
    console.warn("No fee types found in the transaction.");
    throw new Error("No fee types found in the transaction.");
  }

  if (fee_types.length > 1) {
    console.warn("Multiple fee types found in the transaction.");
    throw new Error("Multiple fee types found in the transaction - still to be implemented.");
    // TODO multiple fee types handling
  }

  // 2. Resolve codes for owner, tenant, unit
  const { matched_proprietari, matched_inquilini, matched_unita_immobiliari } = deeperAnalysis;

  let ownerCode: string | undefined = undefined;
  let tenantCode: string | undefined = undefined;
  let unitCode: string = '';


  console.log("Matched owners:", matched_proprietari);
  console.log("Matched tenants:", matched_inquilini);
  console.log("Matched units:", matched_unita_immobiliari);


  // Check if we have at leat one owner or tenant
  if (matched_proprietari.length === 0 && matched_inquilini.length === 0) {
    console.warn("No matching owners or tenants found.");
    throw new Error("No matching owners or tenants found.");
  }
  // Check if we have only one owner when we work on ordinary(GO - gestione ordinaria) or extraordinary (GS - gestione straordinaria) fees
  if (fee_types.includes("GO") || fee_types.includes("GS")) {
    if (matched_proprietari.length === 0) {
      console.warn("No matching owner found for ordinary or extraordinary fees.");
      throw new Error("No matching owner found for ordinary or extraordinary fees.");
    }
    if (matched_proprietari.length > 1) {
      console.warn("Multiple owners found for ordinary or extraordinary fees.");
      throw new Error("Multiple owner found for ordinary or extraordinary fees.");
    }
    if (matched_unita_immobiliari.length > 1) {
      console.warn("Multiple units found for ordinary or extraordinary fees.");
      throw new Error("Multiple units found for ordinary or extraordinary fees.");
    }
    // We have only one tenant, so we can use it
    ownerCode = matched_proprietari[0].cod_condomino;
    unitCode = matched_unita_immobiliari[0].cod_ui;
  }


  if (fee_types.includes("RISC")) {
    if (matched_inquilini.length === 0) {
      console.warn("No matching tenants found for heating fees.");
      throw new Error("No matching tenants found for heating fees.");
    }
    if (matched_inquilini.length > 1) {
      console.warn("Multiple tenants found for heating fees.");
      throw new Error("Multiple tenants found for heating fees.");
    }
    // We have only one tenant, so we can use it
    if (matched_unita_immobiliari.length === 0) {
      console.warn("No matching units found for heating fees.");
      throw new Error("No matching units found for heating fees.");
    }
    if (matched_unita_immobiliari.length > 1) {
      console.warn("Multiple units found for heating fees.");
      throw new Error("Multiple units found for heating fees.");
    }
    // We have only one unit, so we can use it
    unitCode = matched_unita_immobiliari[0].cod_ui;
    console.log("found unit code for RISC type:", unitCode);
    tenantCode = matched_inquilini[0].cod_inquilino;
    ownerCode = matched_unita_immobiliari[0].cod_condomino; // In this case, the tenant is also the owner
    console.log("found tenant code for RISC type:", tenantCode);
  }

  console.log("Resolved codes:", {
    ownerCode,
    tenantCode,
    unitCode,
  });


  // 3. Load relevant scheduled fees and transactions in parallel
  const [
    ordinaryScheduled,
    ordinaryTransactions,
    heatingScheduled,
    heatingTransactions
  ] = await Promise.all([
    fetchAndConvertQuery(condominioId, 'rate', toRate, where('cod_condomino', '==', ownerCode!)),
    fetchAndConvertQuery(condominioId, 'rate_pagate', toRatePagata, where('cod_condomino', '==', ownerCode!)),
    fetchAndConvertQuery(condominioId, 'rate_riscaldamento', toRateRiscaldamento, where('cod_ui', '==', unitCode!)),
    fetchAndConvertQuery(condominioId, 'rate_pagate_riscaldamento', toRateRiscaldamentoPagata, where('cod_ui', '==', unitCode!)),
  ]);
  console.log("Relevant fees and transactions:", { ordinaryScheduled, ordinaryTransactions, heatingScheduled, heatingTransactions });

  // 3.1 Compute next num_rata based on what’s already been paid
  //    For ordinary fees (GO → VO, GS → VS) pull max .num_rata + 1
  //    For heating (RISC) do the same off heatingTransactions
  const feeType = fee_types[0]; // we've already asserted length===1
  let nextNumRata: number;
  if (feeType === 'GO' || feeType === 'GS') {
    // Map GO→VO and GS→VS
    const tipoVers = feeType === 'GO' ? 'VO' : 'VS';
    const maxPaid = ordinaryTransactions
      .filter(r => r.tipo_rata === tipoVers)
      .reduce((m, r) => Math.max(m, r.num_rata), 0);
    nextNumRata = maxPaid + 1;
  } else { // RISC
    const maxPaid = heatingTransactions
      .reduce((m, r) => Math.max(m, r.num_rata), 0);
    nextNumRata = maxPaid + 1;
  }

  // 4. Compute balances using helper
  const balances = computeBalances(ordinaryScheduled, ordinaryTransactions, heatingScheduled, heatingTransactions);
  console.log("Calculated balances with details:", balances);

  let refVers: any = null;



  /** helper to turn Set→array */
  function setsToArrays<T extends Record<string, Set<number>>>(src: T) {
    return Object.fromEntries(
      Object.entries(src).map(([k, set]) => [k, [...set]])
    ) as Record<keyof T, number[]>;
  }
  /* ── 1. build a Set-based bucket for each fee group ──────────────── */
  const groupedSets: Record<FeeKey, Set<number>> = {
    GO: new Set(),
    GS: new Set(),
    RISC: new Set(),
  };
  /* ordinaryScheduled → GO / GS */
  ordinaryScheduled.forEach(({ tipo_rata, num_prev }) => {
    if (tipo_rata === "GO" || tipo_rata === "GS") {
      groupedSets[tipo_rata].add(num_prev);   // ✓ type-narrowed
    }
  });
  /* heatingScheduled  → RISC           */
  heatingScheduled.forEach(({ num_prev }) => {
    groupedSets.RISC.add(num_prev);
  });

  const groupedByType: Record<FeeKey, number[]> = setsToArrays(groupedSets);
  /* ---------- 2. Compute “next” num_prev for each type ------------- */
  const nextPrevByType: Record<FeeKey, number> = {
    GO: Math.max(0, ...groupedSets.GO),
    GS: Math.max(0, ...groupedSets.GS),
    RISC: Math.max(0, ...groupedSets.RISC),
  };

  console.log({ groupedSets, nextPrevByType });




  // Next: generate versamento entry only for single type fees
  if (
    (fee_types.includes("RISC") && fee_types.length === 1)
    || (fee_types.includes("GO") && fee_types.length === 1)
    || (fee_types.includes("GS") && fee_types.length === 1)
  ) {
    refVers = await uploadVersamento(
      fee_types.at(0) as 'GO' | 'GS' | 'RISC',
      condominioId,
      initialAnalysis.amount,
      {
        ownerCode,
        tenantCode,
        unitCode,
        num_rata: nextNumRata,
        num_prev: nextPrevByType[feeType]
      }
    );

    // Fetch the freshly created document
    const snap = await getDoc(refVers);
    if (!snap.exists()) {
      throw new Error("Failed to read back the new document!");
    }

    // snap.data() has all the fields you just wrote (plus any server‐added ones)
    const snapData = snap.data() || {};
    console.log("New versamento:", { id: refVers.id, ...snapData });

    // Return both the ID and the data
    const versamentoDone = { id: refVers.id, data: snap.data() };
    console.log('Versamenti result:', versamentoDone);

    // Create the versamento object based on the type
    if (fee_types[0] === 'RISC') {
      const vr: VersamentoRiscaldamento = {
        id: refVers.id,
        ...(snapData as Omit<VersamentoRiscaldamento, 'id'>),
      };

      const enriched: VersamentoEnriched = {
        ...vr,
        gestione: 'RISC',
        nome: matched_inquilini[0].iq_nom,
        person_id: tenantCode,
        ui_id: unitCode,
      }
      return [enriched];
    } else {
      const vt: VersamentoRate = {
        id: refVers.id,
        ...(snapData as Omit<VersamentoRate, 'id'>),
      };

      const enriched: VersamentoEnriched = {
        ...vt,
        gestione: vt.tipo_rata,
        nome: matched_proprietari[0].ac_nom,
        person_id: ownerCode ?? undefined,
        ui_id: unitCode,
      }
      return [enriched];
    }

  } else {
    console.warn("Multiple fee types found, creating separate versamenti for each type.");
    return [];
  }

}



/**
 * Creates a single versamento document based on fee type
 */
async function uploadVersamento(
  type: 'GO' | 'GS' | 'RISC',
  condominioId: string,
  amount: number,
  context: {
    ownerCode: string | undefined;
    tenantCode: string | undefined;
    unitCode: string;
    num_rata: number;
    num_prev: number;
  }
): Promise<any> {
  let versamentoRef: any;
  if (type === 'RISC') {
    const versObj = {
      cod_con: condominioId,
      cod_ui: context.unitCode,
      data_reg: new Date().toISOString(),
      person_role: 'tenant',
      imp_tot: amount,
      num_prev: context.num_prev,
      num_rata: context.num_rata,
      num_reg: Date.now(),
      progressivo_registrazione: 1,
      tipo_vers: null
    }
    versamentoRef = await addDoc(
      collection(db, 'condomini', condominioId, 'versamenti_riscaldamento'),
      versObj
    );
  } else {
    let typeVers = type === 'GO' ? 'VO' : 'VS';
    const versObj =
    {
      cod_con: condominioId,
      num_prev: context.num_prev,
      tipo_rata: typeVers,
      cod_condomino: context.ownerCode,
      num_rata: context.num_rata,
      imp_tot: amount,
      data_reg: new Date().toISOString(),
      person_role: 'owner',
      num_reg: Date.now(),
      tipo_vers: null,
      progressivo_registrazione: 1,
    }
    versamentoRef = await addDoc(
      collection(db, 'condomini', condominioId, 'versamenti_rate'),
      versObj
    );
  }
  return versamentoRef;
}